import axios from 'axios'
import * as HttpStatus from 'http-status-codes'

import { TaskStatusEnum } from '../components/TaskList/TaskList'
import { toCamelCase } from '../helpers/utils'
import { FleetApplication } from '.'
import { ConnectedIntegration } from './connectedIntegration'
import { LeasingPartner } from './leasingPartner'
import { parsePlaidAccount, PlaidAccount } from './plaid'
import { FeatureId } from './productSubscription'
import { SlimTrailer } from './trailer'
import { SlimTruck } from './truck'
import { InsuranceBeneficiary, SlimUser } from './user'

export enum OnboardingChecklistName {
  SIGN_AGREEMENT = 'SIGN_AGREEMENT',
  SIGN_AGREEMENT_AMENDMENT = 'SIGN_AGREEMENT_AMENDMENT',
  DRIVER_APPLICATION = 'DRIVER_APPLICATION',
  FLEET_APPLICATION = 'FLEET_APPLICATION',
  FLEET_DETAILS = 'FLEET_DETAILS',
  FLEX_AGREEMENT = 'FLEX_AGREEMENT',
  FLEX_STARTER_SUBSCRIPTION = 'FLEX_STARTER_SUBSCRIPTION',
  BACKGROUND_CHECK = 'BACKGROUND_CHECK',
  ADD_DRIVERS = 'ADD_DRIVERS',
  ADD_INDIVIDUAL_DRIVER = 'ADD_INDIVIDUAL_DRIVER',
  TAX_INFO = 'TAX_INFO',
  ELD_SETUP = 'ELD_SETUP',
  EQUIPMENT_SETUP = 'EQUIPMENT_SETUP',
  ADD_INDIVIDUAL_EQUIPMENT = 'ADD_INDIVIDUAL_EQUIPMENT',
  TRUCK_LEASE_APPLICATION = 'TRUCK_LEASE_APPLICATION',
  BROKER_SETUP = 'BROKER_SETUP',
  ADD_INDIVIDUAL_BROKER = 'ADD_INDIVIDUAL_BROKER',
  PAYMENT_PROFILE_SETUP = 'PAYMENT_PROFILE_SETUP',
  BANK_ACCOUNT_INFO = 'BANK_ACCOUNT_INFO',
  CONFIRM_CT_CARD_INFO = 'CONFIRM_CT_CARD_INFO',
  STRIPE_PAYMENT_PROFILE = 'STRIPE_PAYMENT_PROFILE',
  CREDIT_APPLICATION = 'CREDIT_APPLICATION',
  INSURANCE_INFORMATION = 'INSURANCE_INFORMATION',
  INSURANCE_SETUP = 'INSURANCE_SETUP',
  ADD_INDIVIDUAL_INSURANCE_INFORMATION = 'ADD_INDIVIDUAL_INSURANCE_INFORMATION',
  INSURANCE_BENEFICIARY = 'INSURANCE_BENEFICIARY',
  EMPLOYMENT_HISTORY = 'EMPLOYMENT_HISTORY',
  CONTRACTOR_PAYMENTS_PROFILE = 'CONTRACTOR_PAYMENTS_PROFILE',
  PAYROLL_BANK_ACCOUNT = 'PAYROLL_BANK_ACCOUNT',
  ADD_CONTRACTOR = 'ADD_CONTRACTOR',
  CONTRACTOR_PERSONAL_PROFILE = 'CONTRACTOR_PERSONAL_PROFILE',
  CONTRACTOR_PERSONAL_DIRECT_DEPOSIT = 'CONTRACTOR_PERSONAL_DIRECT_DEPOSIT',
  CONTRACTOR_PERSONAL_DEBIT_CARD = 'CONTRACTOR_PERSONAL_DEBIT_CARD',
  MAINTENANCE_SET_ASIDE = 'MAINTENANCE_SET_ASIDE',
  FUEL_CARD = 'FUEL_CARD',
  ONBOARDING_CALL = 'ONBOARDING_CALL',
  ONBOARDING_WEBINAR = 'ONBOARDING_WEBINAR',
  SAFETY_REVIEW = 'SAFETY_REVIEW',
  WELCOME_PACKAGE = 'WELCOME_PACKAGE',
  ACTIVATION = 'ACTIVATION',
  DOT_INSPECTION = 'DOT_INSPECTION',
  PLATES = 'PLATES',
  TRUCK_SETUP = 'TRUCK_SETUP',
  INITIAL_BACKGROUND_CHECK = 'INITIAL_BACKGROUND_CHECK',
  CLEARINGHOUSE_QUERY = 'CLEARINGHOUSE_QUERY',
  CRIMINAL_RECORD_CHECK = 'CRIMINAL_RECORD_CHECK',
  TRUCK_DECALS = 'TRUCK_DECALS',
}

export enum BackgroundCheckVendor {
  CHECKR = 'CHECKR',
  HIRERIGHT = 'HIRERIGHT',
}

export enum OnboardingChecklistStatusDetail {
  IN_REVIEW = 'IN_REVIEW',
  MISSING_INFORMATION = 'MISSING_INFORMATION',
  WRONG_RECIPIENT_FOR_LEASE_AGREEMENT = 'WRONG_RECIPIENT_FOR_LEASE_AGREEMENT',
  MISSING_INFORMATION_FOR_LEASE_AGREEMENT = 'MISSING_INFORMATION_FOR_LEASE_AGREEMENT',
  MISSING_BG_CHECK = 'MISSING_BG_CHECK',
  NOT_SUBMITTED = 'NOT_SUBMITTED',
  CONTACT_CLOUDTRUCKS_REPRESENTATIVE = 'CONTACT_CLOUDTRUCKS_REPRESENTATIVE',
  COMPLETE_PREVIOUS_TASKS = 'COMPLETE_PREVIOUS_TASKS',
  NOT_CASH_USER = 'NOT_CASH_USER',
  FLEET_NOT_ELIGIBLE = 'FLEET_NOT_ELIGIBLE',
  SCREENING_COMPLETED = 'SCREENING_COMPLETED',
  SCREENING_APPOINTMENT_ATTENDED = 'SCREENING_APPOINTMENT_ATTENDED',
  REVIEW_NEEDED = 'REVIEW_NEEDED',
  COMPLETE_DRIVER_APPLICATION = 'COMPLETE_DRIVER_APPLICATION',
  ORDER_CANCELLED = 'ORDER_CANCELLED',
  ORDER_SUSPENDED = 'ORDER_SUSPENDED',
  CONSENT_PENDING = 'CONSENT_PENDING',
  DRIVER_IS_ELIGIBLE = 'DRIVER_IS_ELIGIBLE',
  DRIVER_IS_ONBOARDING = 'DRIVER_IS_ONBOARDING',
  REPORT_ORDERED = 'REPORT_ORDERED',
  REQUEST_DEPOSIT = 'REQUEST_DEPOSIT',
  DEPOSIT_COMPLETE = 'DEPOSIT_COMPLETE',
  DEPOSIT_PENDING = 'DEPOSIT_PENDING',
  DEPOSIT_FAILED = 'DEPOSIT_FAILED',
  DEPOSIT_VERIFICATION_NEEDED = 'DEPOSIT_VERIFICATION_NEEDED',
  PENDING_LEASE_SIGNING = 'PENDING_LEASE_SIGNING',
  COMPLETED = 'COMPLETED',
  TEAM_IS_NTL_INSURANCE_OPTED_IN = 'TEAM_IS_NTL_INSURANCE_OPTED_IN',
  TEAM_IS_OCC_ACC_INSURANCE_OPTED_IN = 'TEAM_IS_OCC_ACC_INSURANCE_OPTED_IN',
  TEAM_IS_NTL_AND_OCC_ACC_INSURANCE_OPTED_IN = 'TEAM_IS_NTL_AND_OCC_ACC_INSURANCE_OPTED_IN',
  NOT_SHIPPED = 'NOT_SHIPPED',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  CRIMINAL_RECORD_CHECK_NOT_COMPLETED = 'CRIMINAL_RECORD_CHECK_NOT_COMPLETED',
  PENDING_PROMISSORY_NOTE_SIGNING = 'PENDING_PROMISSORY_NOTE_SIGNING',
  PENDING_LEASE_APPLICATION = 'PENDING_LEASE_APPLICATION',
  REPORT_EXPIRED = 'REPORT_EXPIRED',
  CLEARINGHOUSE_EXPIRED = 'CLEARINGHOUSE_EXPIRED',
  TRUCK_ASSIGNMENT_NOT_STARTED = 'TRUCK_ASSIGNMENT_NOT_STARTED',
  TRUCK_READY_TO_BE_ASSIGNED = 'TRUCK_READY_TO_BE_ASSIGNED',
}

export enum InsuranceType {
  OCCUPATIONAL_ACCIDENT = 'OCCUPATIONAL_ACCIDENT',
  NON_TRUCKING_LIABILITY = 'NON_TRUCKING_LIABILITY',
  PHYSICAL_DAMAGE = 'PHYSICAL_DAMAGE',
}

export enum DriverScorecardStatus {
  NOT_STARTED = 'NOT_STARTED',
  PENDING_REVIEW = 'PENDING_REVIEW',
  COMPLETED = 'COMPLETED',
}

export interface DriverScorecard {
  id: string
  totalScore: number
  version: 'V1'
  status: DriverScorecardStatus
  attributes: DriverScorecardAttributes
  dateCreated: string
  dateUpdated: string
}

export interface DriverScorecardAttributes {
  yearsEmploymentHistory: number
  longDrivingExperiencePoints: number
  numRecentJobs: number
  jobSwitcherPoints: number
  yearsSinceDrivingIncident: number
  drivingSafetyPoints: number
  hasBankruptcyWithinThreshold: boolean
  hasCivilJudgmentWithinThreshold: boolean
  hasLienWithinThreshold: boolean
  hasCriminalChargeWithinThreshold: boolean
  legalHistoryPoints: number
  creditScoreRating: string
  creditScorePoints: number
}

export interface ChecklistUser extends SlimUser {
  insuranceBeneficiary?: InsuranceBeneficiary
  isTeamAdmin?: boolean
  isTeamDriver?: boolean
}

export interface Context {
  user?: ChecklistUser
  provider?: ConnectedIntegration
  truck?: SlimTruck
  trailer?: SlimTrailer
  fleetApplication?: FleetApplication
  integrations?: ConnectedIntegration[]
  plaidAccount?: PlaidAccount
  backgroundCheckVendor?: BackgroundCheckVendor
  initial_screening_report_url?: string
  secondary_screening_report_url?: string
  sentix_login_url?: string
  insurance_type?: InsuranceType
  eldAccountId?: string
  trackingNumber?: string
  leasingPartner?: LeasingPartner
  driverScorecard?: DriverScorecard
}

export interface OnboardingChecklist {
  name: OnboardingChecklistName
  context?: Context
  isDisabled: boolean
  status: TaskStatusEnum
  statusDetail?: OnboardingChecklistStatusDetail
  subitems: ReadonlyArray<OnboardingSubChecklist>
  clientId?: string
}

export type OnboardingSubChecklist = Omit<OnboardingChecklist, 'subitems'>

export interface OnboardingChecklistResult {
  applicationChecklist?: ReadonlyArray<OnboardingChecklist>
  roadPreparationChecklist?: ReadonlyArray<OnboardingChecklist>
  flexChecklist?: ReadonlyArray<OnboardingChecklist>
  cashChecklist?: ReadonlyArray<OnboardingChecklist>
  onboardingStatus?: {
    applicationPercentage: number
    header: string
    subheader?: string
  }
}

export const shouldShowOnboardingChecklist = (
  checklist: readonly OnboardingChecklist[],
  onboardingChecklist?: readonly OnboardingChecklist[] | null
) => {
  return Boolean(
    onboardingChecklist?.length &&
      checklist
        .filter(
          (c) =>
            ![
              OnboardingChecklistName.BACKGROUND_CHECK, // BACKGROUND_CHECK is also present in onboarding checklist
              OnboardingChecklistName.TRUCK_LEASE_APPLICATION, // TRUCK_LEASE_APPLICATION is also present in onboarding checklist
              OnboardingChecklistName.SIGN_AGREEMENT_AMENDMENT, // SIGN_AGREEMENT_AMENDMENT is hidden in UI
            ].includes(c.name)
        )
        .every((t) => [TaskStatusEnum.IN_PROGRESS, TaskStatusEnum.COMPLETED].includes(t.status))
  )
}

export function fetchUserOnboardingChecklist(
  userId: string,
  featureId?: FeatureId
): Promise<OnboardingChecklistResult | undefined> {
  const url = `/api/v3/users/${userId}/checklist/${featureId ? `?feature_id=${featureId}` : ''}`

  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return {
        ...(response.data.flex_checklist && {
          flexChecklist: parseApplicationChecklist(response.data.flex_checklist),
        }),
        ...(response.data.cash_checklist && {
          cashChecklist: parseApplicationChecklist(response.data.cash_checklist),
        }),
        ...(response.data.application_checklist && {
          applicationChecklist: parseApplicationChecklist(response.data.application_checklist),
        }),
        ...(response.data.road_prep_checklist && {
          roadPreparationChecklist: parseApplicationChecklist(response.data.road_prep_checklist),
        }),
        onboardingStatus: {
          applicationPercentage: response.data.application_percentage,
          header: response.data.header,
          subheader: response.data.subheader,
        },
      }
    }
  })
}

export const fetchUserOnboardingTask = (
  userId: string,
  checklistItemName: OnboardingChecklistName,
  featureId?: FeatureId
): Promise<OnboardingChecklist | undefined> => {
  const params = new URLSearchParams({
    checklist_item_name: checklistItemName,
    ...(featureId ? { feature_id: featureId.toString() } : {}),
  })
  const url = `/api/v3/users/${userId}/checklist-item/?${params.toString()}`
  return axios.get(url).then((response) => {
    if (response.status === HttpStatus.OK) {
      return response.data
    }
  })
}

function parseApplicationChecklist(
  checklist: ReadonlyArray<
    Omit<OnboardingChecklist, 'statusDetail'> & { status_detail: string; is_disabled: boolean }
  >
): ReadonlyArray<OnboardingChecklist> {
  return checklist.map(
    (
      c: Omit<OnboardingChecklist, 'statusDetail'> & { status_detail: string; is_disabled: boolean },
      index: number
    ) => ({
      ...c,
      clientId: `${index}-${c.name}`,
      statusDetail: c.status_detail as OnboardingChecklistStatusDetail,
      context: parseContext(c.context),
      isDisabled: c.is_disabled,
      subitems: c.subitems.map((s: OnboardingSubChecklist, index2: number) => ({
        ...s,
        context: parseContext(s.context),
        clientId: `${index}-${index2}-${s.name}`,
      })),
    })
  )
}

function parseContext(context: any) {
  return {
    ...context,
    ...(context?.fleet_application
      ? { fleetApplication: parseFleetApplication(context.fleet_application) }
      : undefined),
    ...(context?.user ? { user: toCamelCase(context.user) } : undefined),
    ...(context?.truck ? { truck: toCamelCase(context.truck) } : undefined),
    ...(context?.trailer ? { trailer: toCamelCase(context.trailer) } : undefined),
    ...(context?.leasing_partner ? { leasingPartner: toCamelCase(context.leasing_partner) } : undefined),
    ...(context?.driver_scorecard ? { driverScorecard: toCamelCase(context.driver_scorecard) } : undefined),
    ...(context?.plaid_account ? { plaidAccount: parsePlaidAccount(context.plaid_account) } : undefined),
    ...(context?.background_check_vendor
      ? { backgroundCheckVendor: toCamelCase(context.background_check_vendor) }
      : undefined),
    ...(context?.eld_account_id ? { eldAccountId: context.eld_account_id } : undefined),
    ...(context?.tracking_number ? { trackingNumber: context.tracking_number } : undefined),
  }
}

function parseFleetApplication(fleetApplication: any): FleetApplication {
  return {
    businessType: fleetApplication.business_type,
    businessDocumentation: fleetApplication.business_documentation,
    certificateOfInsurance: fleetApplication.certificate_of_insurance,
    operatingAuthorityLetter: fleetApplication.operating_authority_letter,
  }
}

export interface MaintenanceSetAside {
  id: string
  teamId: string
  enabled: boolean
  dateLastEnabled: string
  currentBalance: string
  weeklyMax: string
  jobPercentageMax: string
  totalContributions: string
  totalWithdrawals: string
  userEditable: boolean
  recommendations: {
    jobPercentageMax: number
    weeklyMax: number
    last30DaysMilesDriven: number
  }
}

export function fetchMaintenanceSetAside(): Promise<MaintenanceSetAside | undefined> {
  return axios.get(`/api/v3/cash/maintenance-set-aside/`).then((response) => {
    if (response.status === HttpStatus.OK) {
      return toCamelCase(response.data) as MaintenanceSetAside
    }
  })
}

export function updateMaintenanceSetAside(
  id: string,
  weeklyMax: string
): Promise<MaintenanceSetAside | undefined> {
  return axios
    .put(`/api/v3/cash/maintenance-set-aside/${id}/`, { weekly_max: weeklyMax })
    .then((response) => {
      if (response.status === HttpStatus.OK) {
        return toCamelCase(response.data) as MaintenanceSetAside
      }
    })
}

export function createMaintenanceSetAside(weeklyMax: string): Promise<MaintenanceSetAside | undefined> {
  return axios.post(`/api/v3/cash/maintenance-set-aside/`, { weekly_max: weeklyMax }).then((response) => {
    if (response.status === HttpStatus.OK) {
      return toCamelCase(response.data) as MaintenanceSetAside
    }
  })
}
